import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, setAccessJwt } from './api.js';

interface AuthCtx {
  status: 'unknown' | 'signed_out' | 'signed_in' | 'auth_error';
  email: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}
const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthCtx['status']>('unknown');
  const [email, setEmail] = useState<string | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((expiresAtIso: string) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const ms = Math.max(30_000, new Date(expiresAtIso).getTime() - Date.now() - 60_000);
    refreshTimer.current = setTimeout(async () => {
      try {
        const r = await api.refresh();
        if ('access_jwt' in r) setAccessJwt(r.access_jwt);
        scheduleRefresh(r.expires_at);
      } catch {
        setStatus('auth_error');
        setAccessJwt(null);
      }
    }, ms);
  }, []);

  // Session continuity: attempt silent refresh on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await api.refresh();
        if ('access_jwt' in r) setAccessJwt(r.access_jwt);
        scheduleRefresh(r.expires_at);
        const me = await api.sessionMe();
        setEmail(me.user.email);
        setStatus('signed_in');
      } catch {
        setStatus('signed_out');
      }
    })();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [scheduleRefresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const r = await api.login({ email, password });
      if ('access_jwt' in r) setAccessJwt(r.access_jwt);
      scheduleRefresh(r.expires_at);
      const me = await api.sessionMe();
      setEmail(me.user.email);
      setStatus('signed_in');
    },
    [scheduleRefresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setAccessJwt(null);
      setEmail(null);
      setStatus('signed_out');
    }
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ status, email, signIn, signOut }),
    [status, email, signIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}
